import { FieldArray } from 'react-final-form-arrays';
import { useCallback, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useField } from 'react-final-form';
import { getIn } from 'final-form';

import { Button } from 'UIPackage/MainModule/components/Button';
import { IKnmErknmFormViolation } from 'KnmErknmPackage/ErknmViolationsModule/interfaces/IKnmErknmFormViolation';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { ErknmDecisionViolationItem } from 'KnmPackage/KnmViolatedRequirementsNpa/components/ErknmDecisionViolationItem';
import { ErknmViolationsSelectModal } from 'KnmErknmPackage/ErknmViolationsModule/components/ErknmViolationsSelectModal';
import { ViolationStatusesEnum } from 'APIPackage/KnmModule/enums/ViolationStatusesEnum';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { getAdminFetchSettings } from 'PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { IAdminSetting } from 'APIPackage/AdminModule/interfaces/IAdminSetting';
import { isLaterThanAdminSetting } from 'CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { AdminSettingCodesEnum } from 'APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { getTitleFieldWithRedStar } from 'functions/getTitleFieldWithRedStar';
import { getUserRoleIsSupervisor } from 'AuthPackage/MainModule/selectors/authSelectors';
import { getDecisionDataResigningRequired } from 'DecisionPackage/MainModule/selectors/decisionSelectors';
import { getKnmDataResigningRequired } from 'KnmPackage/MainModule/selectors/knmSelectors';
import { SyncedViolationField } from 'KnmErknmPackage/ErknmViolationsModule/components/SyncedViolationField';
import { ShowMoreButton } from 'UIPackage/MainModule/components/ShowMoreButton/ShowMoreButton';
import { Spinner } from 'UIPackage/MainModule/components/Spinner';

import { useShowMore } from '../../../hooks/useShowMore';
import { getKnmDataSigned } from '../../MainModule/selectors/knmSelectors';
import { getDecisionDataSigned } from '../../../DecisionPackage/MainModule/selectors/decisionSelectors';
import styles from './KnmViolatedRequirementsNpaItem.module.scss';

interface IErknmDecisionViolationItemsProps {
  formRef: IKnmFormRef | undefined;
  name: string;
  watchOnly: boolean;
  actViolations: IKnmErknmFormViolation[];
  isPaperless: boolean;
  isKnm?: boolean;
  isPm?: boolean;
  isDecision?: boolean;
  onChangeFixPlanDate?: (date: string | Date) => void;
  mId: string;
  signed?: boolean;
  isRepeatCheck?: boolean;
  isExpandAll?: boolean;
  extractVersion?: number;
  isProductsResultDecision?: boolean;
}

interface IErknmDecisionViolationItemsStoreProps {
  settings: IAdminSetting[] | undefined;
  isKnmSigned: boolean | null | undefined;
  isDecisionSigned: boolean | null | undefined;
  isSupervisor: boolean;
  resigningRequired?: boolean;
}

const ErknmDecisionViolationItemsWithoutStore = ({
  name,
  watchOnly,
  formRef,
  actViolations,
  settings,
  isPaperless,
  onChangeFixPlanDate,
  mId,
  isKnm,
  isPm,
  isDecision,
  isKnmSigned,
  isDecisionSigned,
  signed,
  isRepeatCheck,
  isSupervisor,
  resigningRequired,
  isExpandAll,
  extractVersion,
  isProductsResultDecision,
}: IErknmDecisionViolationItemsProps & IErknmDecisionViolationItemsStoreProps) => {
  const [isViolationSelectModalVisible, setIsViolationSelectModalVisible] = useState(false);
  const onOpenViolationSelectModal = useCallback(() => setIsViolationSelectModalVisible(true), []);
  const onCloseViolationSelectModal = useCallback(() => setIsViolationSelectModalVisible(false), []);

  const violationsFieldName = `${name}.violations`;
  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;

  // Подписка на значение поля: getFieldState до mount FieldArray возвращает undefined
  // и на первом рендере список «доступных» нарушений получается неверным.
  const { input: violationsField } = useField<IKnmErknmFormViolation[] | undefined>(violationsFieldName, {
    subscription: { value: true },
  });
  const formViolations =
    violationsField.value ??
    (getIn(formRef?.current?.getState().values || {}, violationsFieldName) as IKnmErknmFormViolation[] | undefined) ??
    [];

  const alreadyInListViolationIds = useMemo(() => formViolations.map(violation => violation.id), [formViolations]);

  const violationsForAdding = useMemo(
    () =>
      actViolations.filter(
        actViolation =>
          (actViolation.status === ViolationStatusesEnum.NOT_FIXED || actViolation.status === null) &&
          !alreadyInListViolationIds.includes(actViolation.id),
      ),
    [actViolations, alreadyInListViolationIds],
  );

  const created = formRef?.current.getState().values.created;
  const isLaterThanViolationsInProd = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.VALIDATION_4_1_29_STARTS_FROM,
  );

  const handleAddViolations = useCallback(
    (selectedViolations: IKnmErknmFormViolation[]) => {
      const newViolations = selectedViolations.map(selectedViolation => ({
        ...selectedViolation,
        isNew: true,
        fixPlanDates: !isPaperless
          ? undefined
          : [
              {
                isNew: true,
                value: '',
                guid: null,
                dateCreate: null,
              },
            ],
      }));

      push && newViolations.forEach(violation => push(violationsFieldName, violation));
    },
    [isPaperless, push, violationsFieldName],
  );
  const getRemoveViolationHandler = useCallback(
    index => () => {
      remove && remove(violationsFieldName, index);
    },
    [remove, violationsFieldName],
  );

  const isSigned = isDecision ? isDecisionSigned : isKnmSigned;

  if (!isLaterThanViolationsInProd) {
    return null;
  }

  return (
    <>
      <FieldBlock
        label={getTitleFieldWithRedStar({
          label: 'Нарушения',
          field: violationsFieldName,
          isPm,
          isKnm,
          isDecision,
        })}
        id={`${mId}.violations`}
        button={
          !watchOnly ? (
            <Button
              className="shared-btn-add"
              text="Добавить"
              type="button"
              variant="link"
              color="primary"
              onClick={onOpenViolationSelectModal}
              disabled={!violationsForAdding?.length}
            />
          ) : undefined
        }
      />

      <FieldArray name={violationsFieldName}>
        {({ fields, meta }) => {
          const { visibleItems, hasMore, showMore, totalCount, visibleCount, isRendering } = useShowMore(fields.value, {
            initialCount: 10,
            incrementCount: 30,
          });

          return (
            <>
              {visibleItems.map((violationValue: IKnmErknmFormViolation) => {
                const index = fields.value?.findIndex((item: IKnmErknmFormViolation) => item === violationValue) ?? 0;
                const violationFieldNameName = `${violationsFieldName}[${index}]`;

                return (
                  <SyncedViolationField
                    key={violationFieldNameName}
                    violationFieldName={violationFieldNameName}
                    violationId={violationValue.id ?? '0'}
                  >
                    <ErknmDecisionViolationItem
                      violation={violationValue}
                      fieldName={violationFieldNameName}
                      formRef={formRef}
                      onRemove={getRemoveViolationHandler(index)}
                      watchOnly={watchOnly}
                      isPaperless={isPaperless}
                      onChangeFixPlanDate={onChangeFixPlanDate}
                      mId={`${mId}.violations[${index}]`}
                      isKnm={isKnm}
                      isPm={isPm}
                      isDecision={isDecision}
                      signed={!!signed || !!isSigned}
                      isResultDecisionSinged={signed}
                      extractVersion={extractVersion}
                      isRepeatCheck={isRepeatCheck}
                      isSupervisor={isSupervisor}
                      resigningRequired={resigningRequired}
                      isExpandAll={isExpandAll}
                      violationsLength={totalCount}
                      isProductsResultDecision={isProductsResultDecision}
                    />
                  </SyncedViolationField>
                );
              })}
              {isRendering && <Spinner className={styles.Loader} />}
              {hasMore && !isRendering && (
                <ShowMoreButton onClick={showMore} totalCount={totalCount} visibleCount={visibleCount} />
              )}
              {typeof meta.error === 'string' && (
                <p className="shared-input-error shared-row__small-margin">{meta.error}</p>
              )}
            </>
          );
        }}
      </FieldArray>
      <ErknmViolationsSelectModal
        violations={violationsForAdding}
        visible={isViolationSelectModalVisible}
        onClose={onCloseViolationSelectModal}
        onSubmit={handleAddViolations}
      />
    </>
  );
};

const mapStateToProps = (
  state: IRootState,
  ownProps: IErknmDecisionViolationItemsProps,
): IErknmDecisionViolationItemsStoreProps => ({
  settings: getAdminFetchSettings(state),
  isKnmSigned: getKnmDataSigned(state),
  isDecisionSigned: getDecisionDataSigned(state),
  isSupervisor: getUserRoleIsSupervisor(state),
  resigningRequired: ownProps.isDecision ? getDecisionDataResigningRequired(state) : getKnmDataResigningRequired(state),
});

export const ErknmDecisionViolationItems = connect(mapStateToProps)(ErknmDecisionViolationItemsWithoutStore);
